/* ============================================================================
   Chart.js color tokens — Theme-aware via CSS custom properties
   ============================================================================ */

import { defaults } from 'chart.js';

/** Read a CSS custom property from the document root. */
export function cssVar(name: string, fallback: string): string {
  if (typeof document === 'undefined') return fallback;
  const val = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return val || fallback;
}

export const TEXT = {
  get primary() { return cssVar('--color-text', '#faf8f5'); },
  get muted()   { return cssVar('--color-text-3', '#9a9488'); },
  get faint()   { return cssVar('--color-text-3', '#6b6560'); },
};

export const BORDER = {
  get default() { return cssVar('--color-chart-grid', 'rgba(46, 43, 37, 0.6)'); },
  get subtle()  { return cssVar('--color-chart-grid', 'rgba(46, 43, 37, 0.3)'); },
};

export const BRAND = {
  get gold()      { return cssVar('--color-monarch-gold', '#A4863D'); },
  get goldLight() { return cssVar('--color-monarch-gold-light', '#C4A65D'); },
  get goldDark()  { return cssVar('--color-monarch-gold-dark', '#8A6E2F'); },
};

/** Static data-series colors (same in both themes). */
export const CHART_COLORS = [
  '#A4863D', // gold
  '#10b981', // emerald
  '#3b82f6', // blue
  '#f59e0b', // amber
  '#a855f7', // purple
  '#ef4444', // red
  '#06b6d4', // cyan
  '#ec4899', // pink
];

/** Call after theme change to update Chart.js global defaults. */
export function applyThemeDefaults(): void {
  defaults.color = TEXT.faint;
  defaults.borderColor = BORDER.default;
  defaults.font.family = "'Albert Sans', -apple-system, BlinkMacSystemFont, sans-serif";
  defaults.font.size = 11;
  defaults.plugins.legend.display = false;
  defaults.animation.duration = 600;
  defaults.animation.easing = 'easeOutQuart';

  defaults.plugins.tooltip.backgroundColor = cssVar('--color-tooltip-bg', '#1c1b17ee');
  defaults.plugins.tooltip.titleColor = cssVar('--color-tooltip-text', '#faf8f5');
  defaults.plugins.tooltip.bodyColor = cssVar('--color-tooltip-muted', '#9a9488');
  defaults.plugins.tooltip.borderColor = cssVar('--color-tooltip-border', '#2e2b25');
  defaults.plugins.tooltip.borderWidth = 1;
  defaults.plugins.tooltip.cornerRadius = 8;
  defaults.plugins.tooltip.padding = 10;
}

/** Shared chartTooltip object for per-chart options (reads CSS vars at render time). */
export function getChartTooltip() {
  return {
    backgroundColor: cssVar('--color-tooltip-bg', '#1c1b17ee'),
    titleColor: cssVar('--color-tooltip-text', '#faf8f5'),
    bodyColor: cssVar('--color-tooltip-muted', '#9a9488'),
    borderColor: cssVar('--color-tooltip-border', '#2e2b25'),
    borderWidth: 1,
    cornerRadius: 8,
  };
}
